import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowRightIcon,
  TvIcon
} from '@heroicons/react/24/outline';
import { apiService, Channel, Show, Episode } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAudio } from '../contexts/AudioContext';

const Channels: React.FC = () => {
  const navigate = useNavigate();
  const { startSession } = useAudio();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  // Array of gradient backgrounds for channel and show cards
  const gradientBackgrounds = [
    'bg-gradient-to-br from-purple-500 to-pink-500',
    'bg-gradient-to-br from-blue-500 to-cyan-500',
    'bg-gradient-to-br from-green-500 to-emerald-500',
    'bg-gradient-to-br from-orange-500 to-red-500',
    'bg-gradient-to-br from-indigo-500 to-purple-500',
    'bg-gradient-to-br from-teal-500 to-blue-500',
    'bg-gradient-to-br from-rose-500 to-pink-500',
    'bg-gradient-to-br from-amber-500 to-orange-500',
    'bg-gradient-to-br from-violet-500 to-purple-500',
    'bg-gradient-to-br from-emerald-500 to-teal-500',
    'bg-gradient-to-br from-sky-500 to-blue-500',
    'bg-gradient-to-br from-fuchsia-500 to-pink-500',
    'bg-gradient-to-br from-lime-500 to-green-500',
    'bg-gradient-to-br from-red-500 to-rose-500',
    'bg-gradient-to-br from-cyan-500 to-teal-500',
    'bg-gradient-to-br from-yellow-500 to-amber-500',
    'bg-gradient-to-br from-slate-500 to-gray-500',
    'bg-gradient-to-br from-stone-500 to-neutral-500',
    'bg-gradient-to-br from-zinc-500 to-gray-500',
    'bg-gradient-to-br from-neutral-500 to-slate-500'
  ];

  // Function to get gradient background for a card based on its index
  const getCardGradient = (index: number) => {
    return gradientBackgrounds[index % gradientBackgrounds.length];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsData, showsData, episodesData] = await Promise.all([
          apiService.getChannels(),
          apiService.getShows(),
          apiService.getEpisodes()
        ]);
        setChannels(channelsData);
        setShows(showsData);
        setEpisodes(episodesData);
      } catch (error) {
        console.error('Error fetching channels data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  const getShowsForChannel = (channelId: string) => {
    return shows.filter(show => show.channelId === channelId);
  };

  const getEpisodesForShow = (showId: string) => {
    return episodes.filter(episode => episode.showId === showId);
  };

  const getLatestEpisodeForShow = (showId: string) => {
    const showEpisodes = getEpisodesForShow(showId);
    return showEpisodes.sort((a, b) => 
      new Date(b.publishedAt || b.createdAt).getTime() - 
      new Date(a.publishedAt || a.createdAt).getTime()
    )[0];
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Browse Channels
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Discover channels with curated shows and episodes. Each channel offers unique perspectives on the news.
        </p>
      </div>

      {/* Channels Grid */}
      {channels.length > 0 ? (
        <div className="space-y-8">
          {channels.map((channel, channelIndex) => {
            const channelShows = getShowsForChannel(channel._id);
            
            return (
              <div
                key={channel._id}
                className={`${getCardGradient(channelIndex)} rounded-xl p-6 shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] relative overflow-hidden`}
                onClick={() => navigate(`/channels/${channel._id}`)}
              >
                {/* Semi-transparent overlay for better text readability */}
                <div className="absolute inset-0 bg-white/10 dark:bg-black/30 rounded-xl"></div>
                {/* Channel Header */}
                <div className="relative z-10 mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {channel.logoUrl ? (
                        <img 
                          src={channel.logoUrl} 
                          alt={channel.name}
                          className="w-16 h-16 rounded-lg object-cover ring-2 ring-white/40 dark:ring-white/30"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-white/25 dark:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center ring-2 ring-white/40 dark:ring-white/30">
                          <TvIcon className="w-8 h-8 text-white" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-2xl font-bold text-white dark:text-white drop-shadow-sm">
                          {channel.name}
                        </h2>
                        {channel.description && (
                          <p className="text-white/95 dark:text-white/90 mt-1 drop-shadow-sm">
                            {channel.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm ${
                      channel.status === 'active' 
                        ? 'bg-green-500/30 dark:bg-green-500/20 text-white border border-green-400/50'
                        : 'bg-gray-500/30 dark:bg-gray-500/20 text-white border border-gray-400/50'
                    }`}>
                      {channel.status}
                    </span>
                  </div>
                  
                  {channel.category && (
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-white/25 dark:bg-white/20 backdrop-blur-sm text-white border border-white/40 dark:border-white/30 rounded-full text-sm font-medium">
                        {channel.category}
                      </span>
                      {channel.tags.length > 0 && channel.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-white/20 dark:bg-white/15 backdrop-blur-sm text-white/90 dark:text-white/80 border border-white/30 dark:border-white/20 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Shows in this Channel */}
                {channelShows.length > 0 ? (
                  <div className="relative z-10 space-y-4">
                    <h3 className="text-lg font-semibold text-white dark:text-white drop-shadow-sm">
                      Shows in this Channel
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {channelShows.map((show, showIndex) => {
                        const latestEpisode = getLatestEpisodeForShow(show._id);
                        
                        return (
                          <div
                            key={show._id}
                            className={`${getCardGradient(showIndex + 10)} rounded-lg p-4 hover:shadow-md transition-all duration-300 transform hover:scale-105 cursor-pointer relative overflow-hidden`}
                            onClick={() => navigate(`/channels/${channel._id}/shows/${show._id}`)}
                          >
                            {/* Semi-transparent overlay for show cards */}
                            <div className="absolute inset-0 bg-white/10 dark:bg-black/30 rounded-lg"></div>
                            
                            {/* Show content positioned above overlay */}
                            <div className="relative z-10 mb-3">
                              <h4 className="font-semibold text-white dark:text-white mb-1 drop-shadow-sm">
                                {show.name}
                              </h4>
                              {show.description && (
                                <p className="text-sm text-white/95 dark:text-white/90 line-clamp-2 drop-shadow-sm">
                                  {show.description}
                                </p>
                              )}
                            </div>

                            <div className="relative z-10 space-y-2 mb-4">
                              <div className="flex items-center space-x-2 text-xs text-white/90 dark:text-white/80">
                                <UserGroupIcon className="w-3 h-3" />
                                <span>{show.panelistIds.length} panelists</span>
                              </div>
                              
                              <div className="flex items-center space-x-2 text-xs text-white/90 dark:text-white/80">
                                <ClockIcon className="w-3 h-3" />
                                <span>{show.frequency}</span>
                              </div>

                              {show.category && (
                                <span className="px-2 py-1 bg-white/25 dark:bg-white/20 backdrop-blur-sm text-white border border-white/40 dark:border-white/30 rounded-full text-xs">
                                  {show.category}
                                </span>
                              )}
                            </div>

                            {/* Latest Episode */}
                            {latestEpisode && (
                              <div className="relative z-10 border-t border-white/30 dark:border-white/20 pt-3">
                                <p className="text-xs text-white/90 dark:text-white/80 mb-2">Latest Episode:</p>
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-white dark:text-white line-clamp-1 drop-shadow-sm">
                                      {latestEpisode.title}
                                    </p>
                                    <p className="text-xs text-white/90 dark:text-white/80">
                                      {new Date(latestEpisode.publishedAt || latestEpisode.createdAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                  
                                  <button
                                    onClick={() => handleStartSession(latestEpisode._id, latestEpisode.title)}
                                    disabled={startingSession === latestEpisode._id}
                                    className="ml-2 bg-white/25 dark:bg-white/20 backdrop-blur-sm hover:bg-white/35 dark:hover:bg-white/30 border border-white/40 dark:border-white/30 text-white text-sm font-semibold flex items-center space-x-1 px-3 py-1 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {startingSession === latestEpisode._id ? (
                                      <>
                                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                        <span>Starting...</span>
                                      </>
                                    ) : (
                                      <>
                                        <PlayIcon className="w-4 h-4" />
                                        <span>Play</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* View All Episodes Button */}
                            <div className="relative z-10 mt-3 pt-3 border-t border-white/30 dark:border-white/20">
                              <button
                                onClick={() => navigate(`/channels/${channel._id}/shows/${show._id}`)}
                                className="w-full text-center text-sm text-white/90 dark:text-white/80 hover:text-white font-medium flex items-center justify-center space-x-1 transition-colors duration-300"
                              >
                                <span>View All Episodes</span>
                                <ArrowRightIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="relative z-10 text-center py-8">
                    <TvIcon className="w-12 h-12 text-white/80 mx-auto mb-3" />
                    <p className="text-white/90 dark:text-white/80">
                      No shows available in this channel yet.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <TvIcon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Channels Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            There are no channels available at the moment. Check back soon for fresh content!
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            <ArrowRightIcon className="w-5 h-5" />
            <span>Go Back Home</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Channels;
